/**
 * Libraries & Packages
 */
import Cookies from 'js-cookie';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useContext } from 'react';
import { ErrorManagement } from '@printdeal/error-management/react';

/**
 * Config and Recommendations Config with helpers
 */

// @ts-expect-error
import { getRecommendations } from '@cimpress-technology/recommendation-library';
import { useSelector } from 'react-redux';
import { getConfig } from '../recommendations-helper';
import type { RecommendationsConfig } from '../../../types/Configuration';
import { getRecommendationsConfig } from '../../../helpers/Configuration';

/**
 * Global Contexts and Store
 */
import { RootState } from '../../../store';
// @ts-expect-error
import { selectors as customerSelectors } from '../../../store/slices/customerSlice';
import SegmentContext, { ISegmentContext } from '../../segmentation/SegmentContext';

/**
 * LS & related helpers
 */
import { getFromLocalStorage, LS_KEYS, parseStringifiedValue } from '../../../helpers/window';
import { RecommendationApiResponseFromCT } from '../types';

export const useCTRecommendations = (
  placementId: string,
  componentName: string,
  productId: string | null | undefined = undefined,
  staleTime?: number,
  categoryId?: string | null,
) => {
  const intl = useIntl();
  const accountId: string = useSelector(customerSelectors.getAccountId);
  const { consentData }: ISegmentContext = useContext(SegmentContext);
  const gaClientId = Cookies.get('_ga')?.substring(6);
  /**
   * no need of a useState here to set the user id, can be derived here
   * reading the value of ajs_user_id from LS, to avoid using window.analytics methods
   * accountId is retrieved from customer slice here, as decodeprintdealtoken() is expensive
   * isLoggedIn check is removed as it is not required. Once user is logged in ajs_user_id will have value
   * The value in ajs_user_id will live on in LS even if the user is logged out until LS is purged explicitly
   */
  const userId = accountId || parseStringifiedValue(getFromLocalStorage(LS_KEYS.AJS_USER_ID));
  const anonymousId = getFromLocalStorage(LS_KEYS.ANONYMOUS_ID);
  /**
 * Memoizing the output from the config lambda
 */
  const defaultConfig = useMemo<RecommendationsConfig>(
    () => getRecommendationsConfig() as RecommendationsConfig, [],
  );
  /**
   * Memoizing the config for the current session
   */
  const config = useMemo<RecommendationsConfig | null>(
    () => getConfig(
      defaultConfig,
      componentName,
      intl.locale,
      productId,
      placementId,
      gaClientId,
      userId,
      anonymousId,
    ),
    [defaultConfig, componentName, userId, anonymousId, intl.locale, productId, placementId, gaClientId],
  );

  const anonymousCOAMToken = useSelector<RootState, string | undefined>(
    ({ auth }) => auth.anonymousCOAM?.identity?.anonymousToken,
  );

  /**
   * querying & caching getRecommendations response, with a 2 mins stale time
   * removed all un-necessary useEffects
   */
  const queryKeyForRecentlyViewedPlacement = [`recently-viewed-ct-${placementId}-${productId || categoryId}`];
  const {
    data, isLoading, isError, error, isSuccess,
  } = useQuery<RecommendationApiResponseFromCT>({
    queryKey: queryKeyForRecentlyViewedPlacement,
    queryFn: async () => {
      const recommendations = await getRecommendations(
        { ...config, filterByCategories: categoryId },
        { token: anonymousCOAMToken },
      );
      return recommendations || null;
    },
    enabled: Boolean(userId), // disable the call if no customer Id
    staleTime: staleTime || 0,
  });

  /**
  * Error handling for CT /personalization end points
  */
  if (isError) {
    ErrorManagement.exception({
      exception: error,
      tags: {
        component: 'useCTRecommendation',
        fileLocation: 'components-recommendations-hooks',
        featureFunction: 'useCTRecommendations',
      },
      fingerprint: ['components-recommendations-hooks-useCTRecommendation-results'],
    });
  }

  return {
    data,
    componentName,
    config,
    isError,
    isLoading,
    consentData,
    anonymousCOAMToken,
    isPlacementLoaded: isSuccess,
  };
};
